import { makeObservable, observable } from "mobx";
import AuthStore from "./AuthStore";
import PropertyListStore from "./PropertyListStore";
import PropertyDetailsStore from "./PropertyDetailsStore";
import DeallensStore from "./DeallensStore";
import ExitStrategyStore from "./ExitStrategyStore";
import ProcessTrackerStore from "./ProcessTrackerStore";
import ActingUserDirectoryStore from "./ActingUserDirectoryStore";
import AddedPropertyReportsWatcherStore from "./AddedPropertyReportsWatcherStore";
import ConversationViewerStore from "./ConversationViewerStore";
import InvestorPrefsStore from "./InvestorPrefsStore/InvestorPrefsStore";
import { appBarStore } from "./AppBarStore";
import {
  runCompsProcess,
  runExitStrategyProcess,
  runPropertyDeactivateProcess,
  runQmaProcess,
  runSowProcess,
  runZillowProcess,
} from "@/store/subscriptionCore/processHandlers";
import { PROCESS_TYPE } from "@/store/subscriptionCore/processConstants";
import { invalidateAuthBootstrap } from "@/auth/bootstrapState";
import { clearPropertyShareLinkCache } from "@/pages/PropertyDetail/propertyShareApi";
import { invalidateSessionLoaderState } from "@/routes/sessionLoaderState";

/**
 * Root Store – live entity container and ViewModel coordinator.
 * Owns AuthStore and domain stores; starts lifecycle on start() and coordinates
 * sign-out and customer-change resets so domain stores stay in sync with auth.
 *
 * SECURITY: Single place to reset all stores; prevents stale data across tenant/auth change.
 * ASSUMPTION: start() is called once when the app mounts (e.g. from App.js).
 */
class Store {
  constructor() {
    this.authStore = new AuthStore();
    this.investorPrefsStore = new InvestorPrefsStore(this.authStore);
    this.authStore.investorPrefsStore = this.investorPrefsStore;
    this.propertyListStore = new PropertyListStore();
    this.propertyDetailsStore = new PropertyDetailsStore(this.propertyListStore);
    this.opportunitiesStore = new DeallensStore();
    this.processTrackerStore = new ProcessTrackerStore();
    this.addedPropertyReportsWatcherStore = new AddedPropertyReportsWatcherStore();
    this.conversationViewerStore = new ConversationViewerStore();
    this.exitStrategyStore = new ExitStrategyStore(this.processTrackerStore);
    this.processTrackerStore.registerHandler(PROCESS_TYPE.QMA, runQmaProcess);
    this.processTrackerStore.registerHandler(PROCESS_TYPE.COMPS, runCompsProcess);
    this.processTrackerStore.registerHandler(PROCESS_TYPE.ADD_PROPERTY, runZillowProcess);
    this.processTrackerStore.registerHandler(PROCESS_TYPE.ADD_COMP, runZillowProcess);
    this.processTrackerStore.registerHandler(PROCESS_TYPE.EXIT_STRATEGY, runExitStrategyProcess);
    this.processTrackerStore.registerHandler(PROCESS_TYPE.SOW_AGENT, runSowProcess);
    this.processTrackerStore.registerHandler(PROCESS_TYPE.PROPERTY_DEACTIVATE, (params, context) =>
      runPropertyDeactivateProcess(params, context, {
        propertyDetailsStore: this.propertyDetailsStore,
        opportunitiesStore: this.opportunitiesStore,
        addedPropertyReportsWatcherStore: this.addedPropertyReportsWatcherStore,
      })
    );
    this.actingUserDirectoryStore = new ActingUserDirectoryStore();
    makeObservable(this, {
      authStore: observable,
      investorPrefsStore: observable,
      propertyListStore: observable,
      propertyDetailsStore: observable,
      opportunitiesStore: observable,
      exitStrategyStore: observable,
      processTrackerStore: observable,
      actingUserDirectoryStore: observable,
      addedPropertyReportsWatcherStore: observable,
      conversationViewerStore: observable,
    });
  }

  /**
   * Lifecycle: start all stores (AuthStore hydrates; domain stores react to auth).
   * Call once when the app has mounted (e.g. App useEffect).
   */
  async start() {
    await this.authStore.start();
    this.syncProcessTrackerPersistence();
  }

  /**
   * Pushes auth tenant + user into ProcessTrackerStore for persistence alignment (`syncPersistenceContext`).
   */
  syncProcessTrackerPersistence() {
    const auth = this.authStore;
    const pts = this.processTrackerStore;
    if (!auth || !pts || typeof pts.syncPersistenceContext !== "function") return;
    pts.syncPersistenceContext({
      customer: auth.customer,
      userId: auth.userId,
    });
  }

  /**
   * Reset all domain stores (e.g. after logout). AuthStore state is already cleared by AuthStore.signOut().
   */
  resetAllStores() {
    appBarStore.clear();
    clearPropertyShareLinkCache();
    this.propertyListStore?.reset?.();
    this.propertyDetailsStore?.reset?.();
    this.opportunitiesStore?.reset?.();
    this.exitStrategyStore?.reset?.();
    this.processTrackerStore?.reset?.();
    this.actingUserDirectoryStore?.reset?.();
    this.investorPrefsStore?.loadFromProfile?.(null, { isInvestor: false });
    this.addedPropertyReportsWatcherStore?.reset?.();
    this.conversationViewerStore?.reset?.();
  }

  /**
   * Sign out from Cognito, clear auth state, and reset all domain stores.
   * Call from UI or loaders; navigate to /signin in the caller after this resolves.
   */
  signOut() {
    return this.authStore.signOut().then(() => {
      this.resetAllStores();
      invalidateAuthBootstrap();
      invalidateSessionLoaderState();
    });
  }

  /**
   * Notify domain stores that the active customer changed; they reset and will refetch for the new customer.
   *
   * @param {string} _newCustomer - New customer id (reserved for future use).
   */
  onCustomerChange(_newCustomer) {
    appBarStore.clear();
    clearPropertyShareLinkCache();
    this.propertyListStore?.resetForNewCustomer?.();
    this.propertyDetailsStore?.resetForNewCustomer?.();
    this.opportunitiesStore?.resetForNewCustomer?.();
    this.exitStrategyStore?.resetForNewCustomer?.();
    this.processTrackerStore?.resetForNewCustomer?.();
    this.investorPrefsStore?.loadFromProfile?.(this.authStore?.userProfile, {
      isInvestor: this.authStore?.isInvestor,
    });
    this.syncProcessTrackerPersistence();
    this.addedPropertyReportsWatcherStore?.resetForCustomerChange?.();
    this.conversationViewerStore?.resetForNewCustomer?.();
  }

  /**
   * Notify domain stores that effective identity changed (impersonation start/stop).
   * Leaves appBarStore alone — mounted pages own it via useAppBar; clearing races with
   * async impersonation and can drop actions (e.g. Add Property) until remount.
   */
  onIdentityChange() {
    clearPropertyShareLinkCache();
    this.propertyListStore?.resetForNewCustomer?.();
    this.propertyDetailsStore?.resetForNewCustomer?.();
    this.opportunitiesStore?.resetForNewCustomer?.();
    this.exitStrategyStore?.resetForNewCustomer?.();
    this.processTrackerStore?.resetForNewCustomer?.();
    this.investorPrefsStore?.loadFromProfile?.(this.authStore?.userProfile, {
      isInvestor: this.authStore?.isInvestor,
    });
    this.syncProcessTrackerPersistence();
    this.addedPropertyReportsWatcherStore?.resetForCustomerChange?.();
    this.conversationViewerStore?.resetForNewCustomer?.();
  }

  /**
   * Multi-role view switch (same Cognito user + customer). Resets role-scoped stores only;
   * the property list is tenant/user scoped, not view scoped, so it must survive Realtor ↔ Investor.
   * Leaves appBarStore alone (same ownership as onIdentityChange).
   */
  onActiveRoleChange() {
    clearPropertyShareLinkCache();
    this.propertyDetailsStore?.resetForNewCustomer?.();
    this.opportunitiesStore?.resetForNewCustomer?.();
    this.exitStrategyStore?.resetForNewCustomer?.();
    this.conversationViewerStore?.resetForNewCustomer?.();
    this.investorPrefsStore?.loadFromProfile?.(this.authStore?.userProfile, {
      isInvestor: this.authStore?.isInvestor,
    });
    this.syncProcessTrackerPersistence();
  }
}

const DefaultStore = new Store();
export default DefaultStore;
